import React, { useEffect } from "react";
import { SubmitHandler, useFormContext, UseFormProps } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";
import {
  edsn_Marktrol,
  edsn_organizationalunit_edsn_wijzigingtype,
} from "../../../openapi/edsn-webapi";
import { OrganizationUnitDto } from "../../../openapi/edsn-webapi/models/OrganizationUnitDto";
import { safePropertyMap } from "../../../utils";
import { ean13Validator } from "../../../validators";
import {
  DateControl,
  Form,
  InputControl,
  Tooltip,
  withFormContext,
} from "../../common";
import SubunitEan13SelectControl from "./SubunitEan13SelectControl";
import CheckboxControl from "../../common/Form/CheckboxControl";
import { Alert, Stack, Typography } from "@mui/material";

interface OrganizationUnitFormProps {
  onSubmit: SubmitHandler<OrganizationUnitDto>;
  renderAfter?: React.ReactElement;
  eanDisabled: boolean;
  showFinancialProps?: boolean;
  marketRole: edsn_Marktrol;
  organisationEan13Code: string;
};

interface ExtendedOrganizationUnitDto extends OrganizationUnitDto {
  customValidation?: string;
}

const OrganizationUnitForm = ({
  onSubmit,
  renderAfter,
  eanDisabled,
  showFinancialProps,
  marketRole,
  organisationEan13Code,
}: OrganizationUnitFormProps) => {
  const map = safePropertyMap<OrganizationUnitDto>();
  const intl = useIntl();
  const {
    setError,
    clearErrors,
    watch,
    formState: { errors },
  } = useFormContext<ExtendedOrganizationUnitDto>();
  const changeType = watch("changeType");
  const isAdd =
    changeType === edsn_organizationalunit_edsn_wijzigingtype.TOEVOEGEN;
  const isDelete =
    changeType === edsn_organizationalunit_edsn_wijzigingtype.VERWIJDEREN;
  const genericTooltip = intl.formatMessage(
    { id: "tooltipEan" },
    {
      marketRole: intl.formatMessage({ id: `edsn_Marktrol.${marketRole}` }),
    }
  );
  const showSpecificForm = true;
  const licensedEnergySupplier = watch('licensedSupplier');
  const peerToPeerTrader = watch('peerToPeerTrader');
  const renewableEnergyCommunity = watch('renewableEnergyCommunity');
  const activeCustomer = watch('activeCustomer');
  const injectionAggregator = watch('injectionAggregator');

  useEffect(() => {
    if (!licensedEnergySupplier && !peerToPeerTrader && !renewableEnergyCommunity && !activeCustomer && !injectionAggregator) {
      setError("customValidation", {
        type: "manual",
        message: intl.formatMessage({ id: "atleastOneProductRequired" }),
      });
    } else {
      clearErrors("customValidation");
    }
  }, [licensedEnergySupplier, peerToPeerTrader, renewableEnergyCommunity, activeCustomer, injectionAggregator, setError, clearErrors, intl]);

  return (
    <Form onSubmit={onSubmit}>
      <InputControl required name={map("name")} label="nameMarket" />

      {isAdd ? (
        <InputControl
          required
          disabled={eanDisabled}
          name={map("ean13")}
          label="eanCodeMarket"
          tooltip={genericTooltip}
          tooltipTranslated={!!marketRole}
          validate={(value) => !value || ean13Validator(value) || "invalidEan"}
        />
      ) : (
        <SubunitEan13SelectControl
          tooltip={genericTooltip}
          tooltipTranslated={!!marketRole}
          organisatieEan13Code={organisationEan13Code}
        />
      )}

      {showFinancialProps && !isDelete && (
        <>
          <InputControl
            required
            name={map("vatNumber")}
            label="vatNumb"
            minLength={1}
            maxLength={14}
          />
          <InputControl
            required
            name={map("bankAccount")}
            label="accountNumb"
            tooltip="tooltipAdminPartyAccountNumber"
            pattern={{
              value: /^[A-Z]{2}(\d{2}[A-Z]{1,4})?\d{10,27}$/,
              message: intl.formatMessage({ id: "bankAccountIncorrect" }),
            }}
          />
          {showSpecificForm && (
            <>
              <Typography variant="subtitle1" color="textSecondary">
                <FormattedMessage id={"specificForm"} />
                <Tooltip title="tooltipSpecificForm" />
              </Typography>
              <Stack direction="column">
                <CheckboxControl
                  name={map("licensedSupplier")}
                  label="licensedEnergySupplier"
                />
                <CheckboxControl
                  name={map("peerToPeerTrader")}
                  label="peerToPeerTrader"
                />
                <CheckboxControl
                  name={map("renewableEnergyCommunity")}
                  label="renewableEnergyCommunity"
                />
                <CheckboxControl name={map("activeCustomer")} label="activeCustomer" />
                <CheckboxControl
                  name={map("injectionAggregator")}
                  label="injectionAggregator"
                />
              </Stack>
              {errors.customValidation && (
                <Alert severity="error">
                  <FormattedMessage id="atleastOneFormRequired" />
                </Alert>
              )}
            </>
          )}
        </>
      )}
      {isDelete && <DateControl name={map("endDate")} required />}
      {renderAfter}
    </Form>
  );
};

export default withFormContext<
  UseFormProps<OrganizationUnitDto> & OrganizationUnitFormProps,
  OrganizationUnitDto
>(OrganizationUnitForm);
